import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, FileDown, Pencil } from "lucide-react";
import { toast } from "sonner";
import { parseApiError } from "@/lib/apiErrors";
import RichTextEditor, { RichTextView } from "@/components/RichTextEditor";

/**
 * Reusable Pages tab. Used by both ProjectDetail (`scope="project"`) and
 * ClientDetail (`scope="client"`). Endpoint shape is identical for both — only
 * the URL suffix differs, controlled by the scope.
 *
 * Props:
 *   scope       — "project" | "client"
 *   parentId    — projectId or clientId
 *   pages       — list of page docs (already loaded by the parent)
 *   isAdmin     — whether the current user can delete pages
 *   onChanged   — called after create/update/delete so the parent re-fetches
 */
export default function PagesTab({ scope, parentId, pages, isAdmin, onChanged }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", content: "" });
  const [editingPage, setEditingPage] = useState(null);

  const isClient = scope === "client";
  const resourcePath = isClient ? "client-pages" : "project-pages";
  const parentField = isClient ? "client_id" : "project_id";
  const listAllPdfUrl = isClient
    ? `${process.env.REACT_APP_BACKEND_URL}/api/clients/${parentId}/pages/export-pdf`
    : `${process.env.REACT_APP_BACKEND_URL}/api/projects/${parentId}/pages/export-pdf`;

  const upsert = async () => {
    try {
      if (editingPage) {
        await api.patch(`/${resourcePath}/${editingPage.id}`, form);
      } else {
        await api.post(`/${resourcePath}`, { ...form, [parentField]: parentId });
      }
      setOpen(false); setForm({ title: "", content: "" }); setEditingPage(null);
      onChanged?.();
    } catch (e) { toast.error(parseApiError(e, "Failed to save page")); }
  };

  const del = async (pid) => {
    if (!window.confirm("Delete this page?")) return;
    try {
      await api.delete(`/${resourcePath}/${pid}`);
      onChanged?.();
    } catch (e) { toast.error(parseApiError(e, "Only Admin can delete pages")); }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs text-slate-500 font-mono-data">{pages.length} pages</div>
        <div className="flex items-center gap-2">
          {pages.length > 0 && (
            <a
              href={listAllPdfUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 h-9 px-3 border border-slate-200 hover:border-[#0a2540] hover:text-[#0a2540] rounded-sm text-sm text-slate-600"
              title="Bundle all pages into one PDF"
              data-testid="btn-export-all-pages-pdf"
            >
              <FileDown size={14} /> Export All
            </a>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm" onClick={() => { setEditingPage(null); setForm({ title: "", content: "" }); }} data-testid="btn-new-page">
                <Plus size={14} className="mr-1.5" /> New Page
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] rounded-sm grid-rows-[auto_minmax(0,1fr)_auto] gap-2 overflow-hidden">
              <DialogHeader><DialogTitle style={{ fontFamily: "Outfit" }}>{editingPage ? "Edit Page" : "New Page"}</DialogTitle></DialogHeader>
              <div className="space-y-3 overflow-y-auto pr-1 -mr-1">
                <div>
                  <Label className="text-xs">Title</Label>
                  <Input className="mt-1 rounded-sm" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="input-page-title" />
                </div>
                <div>
                  <Label className="text-xs">Content</Label>
                  <div className="mt-1">
                    <RichTextEditor value={form.content} onChange={(html) => setForm({ ...form, content: html })} placeholder="Write notes, research, drafts…" maxHeight="55vh" />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
                <Button className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm" onClick={upsert} data-testid="btn-save-page">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <div className="space-y-3">
        {pages.length === 0 && <div className="text-xs text-slate-400 text-center py-8 border border-dashed border-slate-200">No pages yet</div>}
        {pages.map((p) => (
          <div key={p.id} className="border border-slate-200 px-4 py-3" data-testid={`page-row-${p.id}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold" style={{ fontFamily: "Outfit" }}>{p.title}</div>
                <div className="text-[10px] text-slate-400 font-mono-data">Updated {new Date(p.updated_at).toLocaleString()}</div>
              </div>
              <div className="flex items-center gap-1">
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/${resourcePath}/${p.id}/export-pdf`}
                  target="_blank"
                  rel="noreferrer"
                  className="h-7 px-2 text-xs inline-flex items-center gap-1 border border-slate-200 hover:border-[#0a2540] hover:text-[#0a2540] rounded-sm text-slate-600"
                  title="Export to PDF"
                  data-testid={`btn-export-page-${p.id}`}
                >
                  <FileDown size={12} /> PDF
                </a>
                <Button size="sm" variant="ghost" className="h-7" onClick={() => { setEditingPage(p); setForm({ title: p.title, content: p.content || "" }); setOpen(true); }} data-testid={`btn-edit-page-${p.id}`}>
                  <Pencil size={12} />
                </Button>
                {isAdmin && (
                  <Button size="sm" variant="ghost" className="h-7 text-red-600" onClick={() => del(p.id)} data-testid={`btn-delete-page-${p.id}`}>
                    <Trash2 size={12} />
                  </Button>
                )}
              </div>
            </div>
            {p.content && <div className="mt-2"><RichTextView html={p.content} /></div>}
          </div>
        ))}
      </div>
    </>
  );
}
